import React, { Fragment, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    useGetUserById,
} from "../../../custom-hooks/use-users";
import {
    Breadcrumbs,
    Button,
    Spinner
} from "@material-tailwind/react";
import {
    convertToPresentableText,
} from "../../../utils/functions";
import {
    Building2Icon,
    HomeIcon,
    LogInIcon,
    UserIcon,
} from "lucide-react";
import { BottomUXSpace } from "../../../components";
import BasicInfo from "./basicInfo";
import MeetingQuota from "./meetingQuota";
import DayPasses from "./daypasses";
import Payments from "./payments";
import Contracts from "./contracts";
import AccountManagement from "./accountManagement";
import TeamMembers from "./teamMembers";
import { URLS } from "../../../router-x/routes";
import { useAuth } from "../../../services/contexts/authContext";
import { usePermissions } from "../../../custom-hooks/use-permissions";
import { STATIC_USER_ROLES } from "../../../utils/enums";


// Functional component
const CustomerDetailsPage = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        data: customerDetails,
        isLoading: isCustomerDetailsLoading,
        error: customerDetailsError,
        // refetch: refetchCustomerDetails,
    } = useGetUserById(customerId);

    const { userDetails, startImpersonation } = useAuth();
    const [impersonating, setImpersonating] = useState(false);

    const customer = useMemo(() => customerDetails?.data?.user || {}, [customerDetails?.data?.user]);

    const { can } = usePermissions();

    const isAdmin = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                    can('FX12-1', 'impersonate');

    const handleImpersonate = async () => {
        try {
            setImpersonating(true);
            // Pass current path so admin is returned here after stopping impersonation
            await startImpersonation(customerId, location.pathname);
            navigate(URLS.CUSTOMER.HOME);
        } catch (err) {
            console.error('Failed to start impersonation:', err);
            setImpersonating(false);
        }
    };

    if (isCustomerDetailsLoading) {
        return (
            <div className={`flex justify-center p-4 m-4 items-center h-screen border rounded-xl`}>
                <Spinner />
            </div>
        );
    }

    if (customerDetailsError) {
        return (
            <Fragment>
                <div className="p-4 flex flex-col justify-center items-center h-96">
                    <div className="p-4 text-red-600 text-center">
                        <h2 className="text-xl font-bold">Error Loading Customer</h2>
                        <p>{customerDetailsError.message || "Failed to load customer details"}</p>
                        <div>
                            <span className="text-gray-500 text-xs">Customer Id: {customerId}</span>
                        </div>
                        <Button className="rounded-full mt-4" variant="gradient" color="gray">
                            <span className="text-sm" onClick={() => navigate(URLS.ADMIN.CUSTOMER)}>
                                Go Back
                            </span>
                        </Button>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <div className="p-4 space-y-6">
            <div className="border rounded-xl p-6">
                <div className="">
                    <Breadcrumbs className="mb-4">
                        <Link to={URLS.ADMIN.ROOT} className="opacity-60 flex items-center gap-1">
                            <HomeIcon size={14} />
                            <span>Home</span>
                        </Link>
                        <Link to={URLS.ADMIN.CUSTOMER} className="opacity-60 flex items-center gap-1">
                            <Building2Icon size={14} />
                            <span>Customers</span>
                        </Link>
                        <span className="flex items-center gap-1">
                            {isCustomerDetailsLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <UserIcon size={14} />
                                    <span>{convertToPresentableText(customer?.name) || 'Customer Details'}</span>
                                </>
                            )}
                        </span>
                    </Breadcrumbs>
                </div>
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">{convertToPresentableText(customer.name) || "Customer Name"}</h1>
                        <p className="text-sm text-gray-500 mt-1">{customer.jobTitle || "Job Title Not Set"}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        {isAdmin && customer.role === STATIC_USER_ROLES.USER && (
                            <button
                                id="impersonate-user-btn"
                                onClick={handleImpersonate}
                                disabled={impersonating}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {impersonating ? (
                                    <Spinner className="h-3 w-3" />
                                ) : (
                                    <LogInIcon size={14} />
                                )}
                                <span>{impersonating ? 'Switching...' : 'Login as this User'}</span>
                            </button>
                        )}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${customer.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                            {customer.isActive ? "Active" : "Inactive"}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Customer - {convertToPresentableText(customer.role)}
                        </span>
                    </div>
                </div>
                <BasicInfo customer={customer} />
                <MeetingQuota customer={customer} />
            </div>
            <DayPasses customer={customer} />
            {can('FX13-1-1', 'R') && <Payments customer={customer} />}
            <Contracts customer={customer} />
            {isAdmin && <AccountManagement customer={customer} />}
            {(can('FX12-1-1', 'view') || can('FX12-1-1', 'edit')) && (
                <TeamMembers customer={customer} />
            )}
            <BottomUXSpace />
        </div>
    );
};


export default CustomerDetailsPage;